import { useCallback, useEffect, useRef, useState } from "react";
import { useDebouncedValue } from "../../lib/useDebouncedValue.js";
import SearchInput from "../ui/SearchInput.jsx";
import { ErrorText } from "../ui/Notice.jsx";
import { MENU_ITEM_ON } from "../ui/styles.js";

const SEARCH_DEBOUNCE_MS = 300;
// Start the next page while a screenful is still below the fold, so scrolling
// never stops at a spinner.
const PREFETCH_PX = 160;

const NOTE = "m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]";

const EMPTY = { rows: [], cursor: null, total: null, done: false };

/**
 * Search-and-pick over a paginated endpoint. `fetchPage({ search, cursor })`
 * returns `{ rows, nextCursor, total }`; a null `nextCursor` ends the list.
 *
 * Every load is tagged with the search that issued it, so a slow page landing
 * after the next keystroke cannot append another search's rows.
 */
export default function PaginatedPicker({
    initialSearch = "",
    placeholder,
    ariaLabel,
    emptyText,
    errorText,
    fetchPage,
    renderRow,
    autoFocus = false,
    // Some feeds refuse to list: the subject candidates are search-only so the
    // endpoint cannot be used to page out the tenant roster. Say so and send
    // nothing rather than letting the server reject an empty search.
    minSearchChars = 0,
    minSearchText = "",
}) {
    const [search, setSearch] = useState(initialSearch);
    const [debounced] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
    const [page, setPage] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const activeSearch = useRef(debounced);
    const inFlight = useRef(false);
    const latestLoad = useRef(0);

    // A new search always supersedes whatever is in flight; only appends
    // deduplicate, so a slow first page cannot swallow the next keystroke.
    const load = useCallback(async (forSearch, cursor) => {
        if (cursor != null && inFlight.current) return;
        const id = latestLoad.current + 1;
        latestLoad.current = id;
        inFlight.current = true;
        activeSearch.current = forSearch;
        setLoading(true);
        setError(null);
        try {
            const result = await fetchPage({ search: forSearch, cursor });
            if (latestLoad.current !== id) return;
            setPage((current) => ({
                rows: cursor == null ? result.rows : [...current.rows, ...result.rows],
                cursor: result.nextCursor ?? null,
                total: result.total ?? null,
                done: result.nextCursor == null,
            }));
        } catch (err) {
            if (latestLoad.current === id) setError(err);
        } finally {
            if (latestLoad.current === id) {
                inFlight.current = false;
                setLoading(false);
            }
        }
    }, [fetchPage]);

    const tooShort = debounced.trim().length < minSearchChars;

    useEffect(() => {
        setPage(EMPTY);
        setError(null);
        activeSearch.current = debounced;
        if (tooShort) {
            setLoading(false);
            return;
        }
        load(debounced, null);
    }, [debounced, tooShort, load]);

    const onScroll = useCallback((event) => {
        if (page.done || loading) return;
        const el = event.currentTarget;
        if (el.scrollTop + el.clientHeight < el.scrollHeight - PREFETCH_PX) return;
        load(activeSearch.current, page.cursor);
    }, [page.done, page.cursor, loading, load]);

    return (
        <div className="grid gap-[6px]">
            <SearchInput
                value={search}
                onChange={setSearch}
                placeholder={placeholder}
                label={ariaLabel}
                autoFocus={autoFocus}
            />
            <div
                onScroll={onScroll}
                aria-busy={loading ? "true" : undefined}
                className="max-h-[240px] overflow-y-auto rounded-[10px] border border-[color:var(--line)] p-[4px]"
            >
                {page.rows.length ? (
                    <>
                        {page.rows.map((row) => renderRow(row))}
                        {loading ? (
                            <p className={`${NOTE} px-[8px] py-[6px]`}>
                                Loading more…
                            </p>
                        ) : null}
                    </>
                ) : (
                    <p className={`${NOTE} px-[8px] py-[8px]`}>
                        {tooShort
                            ? minSearchText
                            : error ? errorText(error) : (loading ? "Searching…" : emptyText)}
                    </p>
                )}
            </div>
            {page.rows.length && error ? <ErrorText>{errorText(error)}</ErrorText> : null}
        </div>
    );
}

export function PickerRow({ primary, secondary, badge = null, selected, onSelect }) {
    return (
        <button
            type="button"
            onClick={onSelect}
            aria-pressed={selected}
            className={`flex w-full cursor-pointer items-center gap-[8px] rounded-[6px] border-0 bg-transparent px-[8px] py-[7px] text-left transition-colors hover:bg-[color-mix(in_srgb,var(--ink)_8%,transparent)] ${selected ? MENU_ITEM_ON : ""}`}
        >
            <span className="min-w-0 flex-1 truncate text-[13px] leading-[1.3] font-medium text-[color:var(--ink)]">
                {primary}
            </span>
            {secondary ? (
                <span className="min-w-0 shrink-[2] truncate text-[12px] leading-[1.3] text-[color:var(--muted)]">
                    {secondary}
                </span>
            ) : null}
            {badge}
        </button>
    );
}
